import { challengeTypeLabel, hasMeaningfulAnswerType } from '@/components/challenges/challengeKinds';
import { QrCodeBlock } from '@/components/qr/QrCodeBlock';
import { RichTextDisplay, StatBar } from '@/components/shared';
import { PinLocationDialog } from '@/components/shared/map/PinLocationDialog';
import { useUpdateChallengePin } from '@/components/challenges/useUpdateChallengePin';
import { cn } from '@/lib/utils';
import type { Challenge } from '@/config/objects/challenges';
import { useChallengeContext } from '@/pages/challenges/useChallengeContext';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MapPreview,
  Skeleton,
  toast,
  useApiClient,
  useApp,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, Hash, MapPin, Pencil, RefreshCw, TriangleAlert, Volume2, X } from 'lucide-react';
import { useState } from 'react';

/** Human labels for the answer types a participant can be asked to submit. */
const ANSWER_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  photo: 'Photo',
  video: 'Video',
  true_false: 'True / False',
  multiple_choice: 'Multiple Choice',
  fill_in_the_blank: 'Fill in the Blank',
  puzzle: 'Puzzle',
};

/** Read-only display of the challenge's greater area (city/region), if any. */
function GreaterAreaDisplay({ area }: { area: Challenge['greaterArea'] }) {
  if (!area) {
    return <p className="text-sm text-muted-foreground italic">No greater area</p>;
  }
  return <Badge variant="secondary">{area.name}</Badge>;
}

function ContentSection({
  title,
  content,
  emptyText = 'Not provided',
}: {
  title: string;
  content?: string | null;
  emptyText?: string;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
      {content ? (
        <RichTextDisplay content={content} />
      ) : (
        <p className="text-sm text-muted-foreground italic">{emptyText}</p>
      )}
    </div>
  );
}

export function ChallengeOverviewTab() {
  const { challenge, isLoading } = useChallengeContext();
  const { config } = useApp();
  const { dbOrganization } = useRequireOrganization();
  const updatePin = useUpdateChallengePin();
  const [editingLocation, setEditingLocation] = useState(false);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const hasLocation = challenge?.latitude && challenge?.longitude;

  const regenerateAudioMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/challenges/${challenge?.id}/regenerate-audio`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Challenge', challenge?.id] });
    },
  });

  const handleRegenerateAudio = () => {
    toast.promise(regenerateAudioMutation.mutateAsync(), {
      loading: 'Generating audio voiceover...',
      success: 'Audio voiceover regenerated',
      error: 'Failed to regenerate audio',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Challenge not found</p>
      </div>
    );
  }

  return (
    <div className={hasLocation ? 'grid lg:grid-cols-3 gap-6' : 'space-y-6'}>
      {/* Main Content - Left Side */}
      <div className={hasLocation ? 'lg:col-span-2 space-y-6' : 'space-y-6'}>
        {/* Stats Bar */}
        <StatBar
          items={[
            { label: 'Points', value: challenge.points, icon: Hash },
            {
              // For roadblock + minigames the answer type is meaningless, so we
              // show the challenge format (e.g. "Memory Match") instead of "Text".
              label: hasMeaningfulAnswerType(challenge.type) ? 'Answer Type' : 'Format',
              value: hasMeaningfulAnswerType(challenge.type)
                ? (ANSWER_TYPE_LABELS[challenge.answerType] ?? challenge.answerType)
                : challengeTypeLabel(challenge.type),
              icon: Camera,
            },
            { label: 'Zone', value: challenge.zone || 'Not set', icon: MapPin },
          ]}
        />

        {/* Content */}
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Content</CardTitle>
            <CardDescription>Clue, hint, and answer</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <ContentSection title="Clue" content={challenge.clue} />

            {/* Voiceover — narration of the clue, sits directly under it. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-muted-foreground">Voiceover</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerateAudio}
                  disabled={regenerateAudioMutation.isPending}
                >
                  <RefreshCw
                    className={cn('h-4 w-4', regenerateAudioMutation.isPending && 'animate-spin')}
                  />
                  {challenge.audioUrl ? 'Regenerate' : 'Generate'}
                </Button>
              </div>
              {challenge.voiceoverStale && challenge.audioUrl && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    The clue has changed since this voiceover was generated. Regenerate to match the
                    current clue.
                  </span>
                </div>
              )}
              {challenge.audioUrl ? (
                <div className="flex items-center gap-3">
                  <Volume2 className="h-5 w-5 text-muted-foreground shrink-0" />
                  <audio controls src={challenge.audioUrl} className="w-full" />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No voiceover generated yet. Click &quot;Generate&quot; to create one.
                </p>
              )}
            </div>

            <ContentSection title="Hint" content={challenge.hint} />
            {/* Only show the Answer when there is one. Photo (AI-validated),
                puzzle, roadblock, and the minigames all have no text answer —
                they're played/graded elsewhere (see the Demo tab). */}
            {challenge.answer && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Answer</h4>
                <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg">
                  {challenge.answer}
                </p>
              </div>
            )}
            {challenge.alternateAnswers && challenge.alternateAnswers.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Alternate Answers</h4>
                <div className="flex flex-wrap gap-2">
                  {challenge.alternateAnswers.map((answer, index) => (
                    <span
                      key={index}
                      className="text-sm font-mono bg-muted/50 px-3 py-1 rounded-lg"
                    >
                      {answer}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {challenge.answerType === 'multiple_choice' && challenge.choices && challenge.choices.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Choices</h4>
                <div className="space-y-2">
                  {challenge.choices.map((choice, index) => {
                    const isCorrect = choice === challenge.answer;
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-2 text-sm font-mono px-3 py-2 rounded-lg ${
                          isCorrect
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                            : 'bg-muted/50 text-muted-foreground'
                        }`}
                      >
                        {isCorrect ? (
                          <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                        )}
                        {choice}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Access Code — passcode + scannable QR teams use to unlock the challenge. */}
        {challenge.accessCode && (
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Access Code</CardTitle>
              <CardDescription>Teams type this code or scan the QR to unlock</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <QrCodeBlock
                  value={challenge.accessCode}
                  label={challenge.name}
                  size={140}
                  alt={`QR for access code ${challenge.accessCode}`}
                />
                <div className="space-y-1">
                  <p className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">Code</p>
                  <p className="font-mono text-lg font-semibold">{challenge.accessCode}</p>
                  {challenge.accessCodeMessage && (
                    <p className="text-sm text-muted-foreground">{challenge.accessCodeMessage}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Location - only show as inline card if no lat/lng */}
        {!hasLocation && challenge.zone && (
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Location</CardTitle>
              <CardDescription>Geographic information</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Zone</p>
                <p className="text-sm font-medium">{challenge.zone}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* When there's no map, the greater area lives in the main column. */}
        {!hasLocation && (
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Greater Area</CardTitle>
              <CardDescription>City / region</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <GreaterAreaDisplay area={challenge.greaterArea} />
            </CardContent>
          </Card>
        )}

        {/* No pin yet — let the owner drop one on the map. */}
        {!hasLocation && challenge.organizationId === dbOrganization?.id && (
          <Button variant="outline" size="sm" icon={MapPin} onClick={() => setEditingLocation(true)}>
            Set location
          </Button>
        )}
      </div>

      {/* Location Card with Map - Right Side */}
      {hasLocation && (
        <div className="lg:col-span-1">
          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            <MapPreview
              latitude={challenge.latitude}
              longitude={challenge.longitude}
              googleMapsApiKey={config?.googleMapsApiKey}
              alt={`Map of ${challenge.name}`}
              height="h-48"
              className="rounded-none"
              fallbackText="Map unavailable"
            />
            <div className="p-4 space-y-3">
              {challenge.zone && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Zone</p>
                  <p className="text-sm font-medium">{challenge.zone}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Latitude</p>
                  <p className="text-sm font-mono">{challenge.latitude}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Longitude</p>
                  <p className="text-sm font-mono">{challenge.longitude}</p>
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs text-muted-foreground mb-1.5">Greater Area</p>
                <GreaterAreaDisplay area={challenge.greaterArea} />
              </div>
              {challenge.organizationId === dbOrganization?.id && (
                <div className="border-t pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Pencil}
                    onClick={() => setEditingLocation(true)}
                  >
                    Edit location
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PinLocationDialog
        open={editingLocation}
        onOpenChange={setEditingLocation}
        latitude={challenge.latitude ?? null}
        longitude={challenge.longitude ?? null}
        googleMapsApiKey={config?.googleMapsApiKey}
        saving={updatePin.isPending}
        onSave={(latitude, longitude) =>
          updatePin.mutate(
            { challengeId: challenge.id, latitude, longitude },
            { onSuccess: () => setEditingLocation(false) },
          )
        }
      />
    </div>
  );
}
